/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useMemo } from 'react';

interface RichContentRendererProps {
  content: string;
  className?: string;
  maxHeight?: string; // Optional scrollable maximum height
  lightTheme?: boolean; // Optional flag to render in light-friendly colors
}

/**
 * Safely sanitizes an HTML string to prevent XSS attacks while keeping formatting tags.
 */
function sanitizeHTML(html: string): string {
  if (!html) return '';

  let sanitized = html;

  // 1. Strip script tags and their content
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // 2. Strip iframe, object, embed, frame, frameset, applet, head, html, body, meta, link (except safe stylesheet if needed)
  sanitized = sanitized.replace(/<(iframe|object|embed|frame|frameset|applet|meta|form|input|button|style)\b[^>]*>([\s\S]*?)<\/\1>/gi, '');
  sanitized = sanitized.replace(/<(iframe|object|embed|frame|frameset|applet|meta|form|input|button|style|link)\b[^>]*\/?>/gi, '');

  // 3. Strip all "on*" event handler attributes (e.g., onclick, onerror, onload, etc.)
  sanitized = sanitized.replace(/\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)/gi, '');

  // 4. Strip "javascript:" or "data:" URIs from href or src attributes
  sanitized = sanitized.replace(/href\s*=\s*["']\s*javascript:[^"']*["']/gi, 'href="#"');
  sanitized = sanitized.replace(/src\s*=\s*["']\s*javascript:[^"']*["']/gi, 'src="#"');

  return sanitized;
}

/**
 * Parses basic Markdown syntax and returns corresponding HTML.
 */
function markdownToHTML(markdown: string): string {
  if (!markdown) return '';

  let html = markdown;

  // Normalize line endings
  html = html.replace(/\r\n/g, '\n');

  // 1. Code blocks: ```code```
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="bg-slate-950/90 border border-slate-800 p-4 rounded-xl font-mono text-xs overflow-x-auto my-4 text-slate-300"><code>${code.trim()}</code></pre>`;
  });
  html = html.replace(/```([\s\S]*?)```/g, (_, code) => {
    return `<pre class="bg-slate-950/90 border border-slate-800 p-4 rounded-xl font-mono text-xs overflow-x-auto my-4 text-slate-300"><code>${code.trim()}</code></pre>`;
  });

  // 2. Inline code: `code`
  html = html.replace(/`([^`\n]+)`/g, '<code class="bg-slate-950/80 border border-slate-800/60 px-1.5 py-0.5 rounded font-mono text-amber-400 text-xs">$1</code>');

  // 3. Headings
  html = html.replace(/^#### (.*?)$/gm, '<h4 class="text-sm font-sans font-black text-amber-400 mt-4 mb-2">$1</h4>');
  html = html.replace(/^### (.*?)$/gm, '<h3 class="text-base font-sans font-black text-slate-100 mt-5 mb-2">$1</h3>');
  html = html.replace(/^## (.*?)$/gm, '<h2 class="text-lg font-sans font-black text-slate-100 mt-6 mb-3">$1</h2>');
  html = html.replace(/^# (.*?)$/gm, '<h1 class="text-xl font-sans font-black text-slate-100 mt-7 mb-4 border-b border-slate-800 pb-2">$1</h1>');

  // 4. Bold / Italic
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

  // 5. Images: ![alt](url)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="rounded-xl border border-slate-800 max-w-full my-4" referrerPolicy="no-referrer" />');

  // 6. Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-amber-400 hover:text-amber-300 hover:underline inline-flex items-center gap-0.5">$1</a>');

  // 7. Unordered Lists: convert - item or * item
  // Process block-level conversions
  const lines = html.split('\n');
  let inUl = false;
  let inOl = false;
  let inQuote = false;

  const processedLines = lines.map((line) => {
    let trimmed = line.trim();

    // Blockquote: > text
    if (trimmed.startsWith('>')) {
      const quoteText = trimmed.substring(1).trim();
      let prefix = '';
      let suffix = '';
      if (!inQuote) {
        inQuote = true;
        prefix = '<blockquote class="border-l-4 border-amber-500 bg-slate-900/40 pl-4 py-2 my-3 italic text-slate-400">';
      }
      if (inUl) { inUl = false; suffix += '</ul>'; }
      if (inOl) { inOl = false; suffix += '</ol>'; }
      return `${suffix}${prefix}${quoteText}`;
    } else if (inQuote && trimmed === '') {
      inQuote = false;
      return '</blockquote>';
    }

    // Unordered list: - or *
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('● ')) {
      const itemText = trimmed.replace(/^[-*●]\s+/, '');
      let prefix = '';
      let suffix = '';
      if (!inUl) {
        inUl = true;
        prefix = '<ul class="list-disc pl-5 my-3 space-y-1.5 text-slate-300 font-sans">';
      }
      if (inOl) { inOl = false; suffix = '</ol>'; }
      if (inQuote) { inQuote = false; suffix += '</blockquote>'; }
      return `${suffix}${prefix}<li>${itemText}</li>`;
    }

    // Ordered list: 1. or 2.
    if (/^\d+\.\s+/.test(trimmed)) {
      const itemText = trimmed.replace(/^\d+\.\s+/, '');
      let prefix = '';
      let suffix = '';
      if (!inOl) {
        inOl = true;
        prefix = '<ol class="list-decimal pl-5 my-3 space-y-1.5 text-slate-300 font-sans">';
      }
      if (inUl) { inUl = false; suffix = '</ul>'; }
      if (inQuote) { inQuote = false; suffix += '</blockquote>'; }
      return `${suffix}${prefix}<li>${itemText}</li>`;
    }

    // Close open blocks if line is empty or doesn't match list patterns
    let closeBlocks = '';
    if (trimmed === '' || (!trimmed.startsWith('- ') && !trimmed.startsWith('* ') && !trimmed.startsWith('● ') && !/^\d+\.\s+/.test(trimmed))) {
      if (inUl) { inUl = false; closeBlocks += '</ul>'; }
      if (inOl) { inOl = false; closeBlocks += '</ol>'; }
      if (inQuote) { inQuote = false; closeBlocks += '</blockquote>'; }
    }

    if (trimmed === '') {
      return `${closeBlocks}<br/>`;
    }

    // Wrap plain paragraphs
    if (closeBlocks === '' && !trimmed.startsWith('<h') && !trimmed.startsWith('<pre') && !trimmed.startsWith('<blockquote') && !trimmed.startsWith('<ul') && !trimmed.startsWith('<ol')) {
      return `<p class="leading-relaxed mb-3 text-slate-300 font-sans">${trimmed}</p>`;
    }

    return `${closeBlocks}${line}`;
  });

  // Close any trailing blocks
  let suffix = '';
  if (inUl) suffix += '</ul>';
  if (inOl) suffix += '</ol>';
  if (inQuote) suffix += '</blockquote>';

  return processedLines.join('\n') + suffix;
}

export default function RichContentRenderer({ content, className = '', maxHeight, lightTheme }: RichContentRendererProps) {
  const renderedHTML = useMemo(() => {
    if (!content) return '';

    // Step 1: Detect if content is HTML
    const hasHTMLTags = /<[a-z][\s\S]*>/i.test(content) || content.includes('<br') || content.includes('&nbsp;');

    let parsedHTML = '';
    if (hasHTMLTags) {
      // Content is HTML
      parsedHTML = content;
    } else {
      // Check if it's likely Markdown
      const hasMarkdown = /[\*_`#\[\]\!]/.test(content) || content.includes('\n-') || content.includes('\n*');
      if (hasMarkdown) {
        parsedHTML = markdownToHTML(content);
      } else {
        // Content is plain text, render with paragraphs split by double newlines or single newlines
        parsedHTML = content
          .trim()
          .split('\n\n')
          .map(para => `<p class="leading-relaxed mb-3 text-slate-300 font-sans">${para.replace(/\n/g, '<br/>')}</p>`)
          .join('\n');
      }
    }

    // Step 2: Sanitize to prevent XSS
    let sanitized = sanitizeHTML(parsedHTML);

    // Step 3: Support lightTheme by adjusting text classes
    if (lightTheme) {
      sanitized = sanitized.replace(/text-slate-300/g, 'text-slate-800');
    }

    return sanitized;
  }, [content, lightTheme]);

  return (
    <div
      className={`${lightTheme ? 'prose-slate text-slate-800' : 'prose-invert prose-amber text-slate-300'} prose max-w-none font-sans text-xs sm:text-sm leading-relaxed space-y-1.5 break-words ${maxHeight ? `${maxHeight} overflow-y-auto pr-1.5 custom-scrollbar` : ''} ${className}`}
      dangerouslySetInnerHTML={{ __html: renderedHTML }}
    />
  );
}
